<template>
    <viewer-tab ref="tab" title="Shaper Modeler" v-loading="loading" element-loading-text="Saving"
                :typename="$options.typename" :lib-obj="libObject" :url-help="$urls.HELP_MODEL_SHAPER"
                :accept="handleAccept" :click-x="clickX" @active="handleTabActive">
        <div class="pane-grid-1-2">
            <div class="pane-1">
                <!--INFO-->
                <ModelerInfo ref="modelerInfo" :form-info="formInfo"/>
                <!--GEOMETRY-->
                <div class="section-heading">
                    <h4>Geometry</h4>
                    <hr>
                </div>
                <FormShaperGeom
                        ref="formGeom"
                        :form="formGeom"
                        :label-width="$options.labelWidth"
                        :units="$options.units"
                        @change="changeGeom"
                        @change-tip-max="changeTipMax"/>
            </div>
            <div class="pane-2 pane-2-chart chart-container" v-loading="loadingGraphics" element-loading-text="Computing cutter">
                <!--https://www.chartjs.org/docs/latest/general/responsive.html-->
                <!--WARNING: Canvas element is set to chart-container size regardless of other elements.-->
                <small id="plot-units">Axes in millimeters</small>
                <canvas ref="plot"/>
            </div>
            <div class="pane-3 pane-3-tables">
                <modeler-tables :tables="tables"/>
            </div>
        </div>
    </viewer-tab>
</template>

<script>
  import ViewerTab from "../viewer/ViewerTab";
  import ModelerTables from "../_tables/ModelerTables";
  import ModelerInfo from "../_forms/ModelerInfo";
  import FormShaperGeom from "../_forms/FormShaperGeom";
  import FormSharing from "../_forms/FormSharing";
  import math from "../../assets/js/math";
  import {assignProps, copyDeep, copyDeepJSON, ResizeListener} from "../../assets/js/utils";
  import { points } from "../../assets/js/coord";
  import Chart from "chart.js";
  import { chartResize } from "../../assets/js/chart/helper";
  import { pinionCutterCoordinates } from "../../assets/js/chart/tool";
  import {apolloQuery} from "../../config/apollo";
  import requests from "../../requests";
  import gql from "graphql-tag";

  export default {
    name: "ModelShaper",
    components: {ModelerTables, FormShaperGeom, ModelerInfo, ViewerTab, FormSharing},
    props: {
      clickX: Function,
      openId: String,
      name: String,  // unique tab name
    },

    // STATIC DATA
    typename: requests.tool.TYPENAME.PINION_CUTTER,
    labelWidth: {
      col1: '135px',
      col2: '135px',
    },
    units: {
      angle: 'deg',
    },
    plot: {
      // 197 points tuned per:  6 * 16 [involute] + 6 * 11 [tip corner] + 4 * 5 [root] + 3 * 5 [tip]
      nullData: math.linspace(-5, 5, 197).map(x => {return {x: x, y: 0}}),
    },

    data() {
      return {
        // APOLLO
        loading: false,
        libObject: null,

        // FORMS
        formInfo: {
          name: '',
          number: '',
          description: '',
        },
        formGeom: {
          addendumCoefficient: '1.25',
          dedendumCoefficient: '1',
          helixAngle: '0',
          moduleNormal: '1',
          numberOfTeeth: '27',
          pressureAngleNormal: '20',
          profileShiftCoefficient: '0',
          protuberanceSize: '0',
          tipRadiusCoefficient: '0.25',
          tipRadiusMax: false,
          // tooth form fields
          toothThicknessNormal: '',
          rootDiameter: '',
          tipDiameter: '',
        },

        // TABLE
        docSharing: {},  // owner, shared, lock
        docSigned: {},  // lastModifiedOn, createdOn, etc.

        // PLOT
        chart: null,
        loadingGraphics: false,
        resizeListener: null,
      }
    },
    computed: {
      isEditing() {
        // checks if editing an existing object
        return typeof this.openId !== 'undefined';
      },
      tableFile() {
        return this.$handlers.lib.tableFile({
          ...this.formInfo,
          ...this.docSharing,
          ...this.docSigned,
          owner: this.$store.state.session.user.Id,
        });
      },
      tableGeom() {
        return this.$handlers.tool.tablePinionCutterGeom({geom: this.formGeom}, this.$options.units);
      },
      tables() {
        return [{
          table: this.tableGeom,
          title: 'Geometry',
        }, {
          table: this.tableFile,
          title: 'File',
        }]
      },
    },
    apollo: {
      libObject: {
        skip: true,
        fetchPolicy: 'cache-and-network',
        variables() { return { Id: this.openId } },
        query: gql`query getPinionCutter ($Id: ID!) {
            libObject: pinionCutter (Id: $Id) {
              ${requests.lib.PinionCutter}
            }
          }`,
        result({ data }) {
          // deep copy to avoid mutation of Apollo cache data
          let libObj = copyDeep(data.libObject);
          this.$handlers.tool.convertPinionCutterGeom(libObj, undefined, this.$options.units);

          // DEV ONLY
          // this.$message.warning(String(libObject.geom.pressureAngle));

          // map lib object to form data, then update plot
          [this.formInfo, this.docSharing, this.docSigned, this.formGeom] = this.$handlers.lib.libObjectForms(libObj);

          // update plot and form tracker; timeout due to chart resize not always executing
          setTimeout(async () => {
            await this.updatePlot(true);
          }, 100);
        },
      },
    },

    mounted() {
      // initialize plot
      this.newPlot();

      // if editing existing item, start watch query to map form data and update plot
      // watch query used to reactively update to changes from sharing dialog
      if (this.isEditing) {
        this.$apollo.queries.libObject.skip = false;
      } else {
        // timeout due to chart resize not always executing; $nextTick did not help
        setTimeout(async () => {
          await this.updatePlot(true);
        }, 275)
      }
    },
    methods: {
      handleTabActive() {
        setTimeout(this.onViewerResize, 50);
      },

      // APOLLO
      mapForms2Geom() {
        // Map forms to pinion cutter geom schema.
        // The technique here is used for consistency with more complex forms, such as detailed gears.

        // initialize
        let geom = copyDeepJSON(this.$requests.tool.PinionCutterGeomInput);

        // assign form data to geom
        assignProps(geom, this.formGeom);

        return geom;
      },
      async queryCompute(geom) {
        // API query of tip radius parameters.
        // arg: PinionCutterGeomInput type, with angular units in radians.
        // return: Computed tool geometry for tip radius.
        // PLACEHOLDER {server-side errors} display runtime errors for invalid geometry, e.g. tip radius too large
        //  - Display in pane-3 or at least in this.$message().

        this.loadingGraphics = true;
        const query = {
          errorPolicy: "ignore",
          fetchPolicy: "network-only",
          variables: {
            data: geom,
          },
          query: gql`query computePinionCutter($data: PinionCutterGeomInput!) {
            computePinionCutter(data: $data) {
              compute {
                ${this.$requests.eng.PinionCutterCompute}
              }
            }
          }`,
        };
        const {data: {computePinionCutter}} = await apolloQuery(query).finally(() => this.loadingGraphics = false);

        return computePinionCutter.compute;
      },
      async mutateCreate() {
        // API mutation to create new pinion cutter.
        this.loading = true;

        const mutate = {
          variables: {
            data: {
              ...this.formInfo,
              lock: true,
              shared: [],
              geom: this.mapForms2Geom()
            },
          },
          mutation: gql`mutation createPinionCutter ($data: PinionCutterInput!) {
            createPinionCutter (data: $data) {
              pinionCutter {
                ${this.$requests.lib.pinionCutterCollectionFields}
              }
            }
          }`,
          update: (cache, { data: { createPinionCutter: { pinionCutter } } }) => {
            // Update cache of collection to avoid a network refetch.

            const query = {
              variables: {
                limit: this.$requests.lib.LIMIT
              },
              query: gql`query getPinionCutters ($limit: Int) {
                pinionCutters (limit: $limit) {
                  __typename
                  ${this.$requests.lib.pinionCutterCollectionFields}
                }
              }`,
            };

            // Read the data from our cache for this query.
            const data = cache.readQuery(query);
            // Modify the query data object.
            data.pinionCutters.unshift(pinionCutter);
            // Write modified data back to the cache.
            cache.writeQuery({ ...query, data })
          },
        };

        await this.$gql.mutateConvertUnits(mutate, this.$options.units)
          .finally(() => this.loading = false);
      },
      async mutateUpdate(Id) {
        // API mutation to update existing pinion cutter.
        this.loading = true;

        const mutate = {
          variables: {
            data: {
              Id: Id,
              ...this.formInfo,
              geom: this.mapForms2Geom()
            },
          },
          mutation: gql`mutation updatePinionCutter ($data: UpdatePinionCutterInput!) {
            updatePinionCutter (data: $data) {
              pinionCutter {
                ${this.$requests.lib.PinionCutter}
              }
            }
          }`,
        };

        await this.$gql.mutateConvertUnits(mutate, this.$options.units)
          .finally(() => this.loading = false);
      },

      // FORM
      changeGeom(valid) {
        // A geometric input has changed.  Update plot.
        // arg: Boolean to indicate form validation.

        if (valid) {
          this.updatePlot();
        } else {
          this.resetPlot();
        }
      },
      changeTipMax(valid) {
        // Max tool tip checkbox has changed.  If max is selected, update plot.
        // arg: Boolean to indicate form validation.

        if (valid && this.formGeom.tipRadiusMax) this.updatePlot();
      },
      async validateAll() {
        // Returns true if all forms are valid, otherwise display error message and return false.
        // Beware the number zero can cause issues that do not catch; but string zero is okay.
        // No validation required for the sharing form.

        try {
          await this.$refs.modelerInfo.validate();
          await this.$refs.formGeom.validate();
          return true;
        } catch (e) {
          this.$message({
            type: 'error',
            center: true,
            message: 'Must correct errors before saving',
          });
          return false;
        }
      },
      async handleCreate() {
        // Create a new pinion cutter if all forms are valid.

        // validate, otherwise catch
        const valid = await this.validateAll();

        if (valid) {
          // API mutation, then close tab
          await this.mutateCreate();
          this.handleClose();
          this.$notify.success({
            position: this.$defaults.notifyPosition,
            title: 'Created Shaper',
            message: this.formInfo.name + ' added to library',
          });
        }
      },
      async handleUpdate() {
        // Update the pinion cutter if all forms are valid.

        // validate, otherwise catch
        const valid = await this.validateAll();

        if (valid) {
          // API mutation, then close tab
          await this.mutateUpdate(this.openId);
          this.handleClose();
          this.$notify.success({
            position: this.$defaults.notifyPosition,
            title: 'Updated Shaper',
            message: this.formInfo.name + ' saved to library',
          });
        }
      },
      async handleAccept() {
        return this.isEditing ? await this.handleUpdate() : await this.handleCreate();
      },
      handleClose() {
        // Call close method on closable tab to play transition.
        // https://stackoverflow.com/questions/40957008/how-to-access-to-a-child-method-from-the-parent-in-vue-js/40957171
        this.$refs.tab.handleClose();
      },

      // PLOT
      newPlot() {
        let ctx = this.$refs.plot.getContext('2d');
        this.chart = new Chart(ctx, {
          type: 'line',
          data: {
            datasets: [{
              ...this.$defaults.chart.lineDataset,
              fill: 'end',
              data: this.$options.plot.nullData,
            }]
          },
          options: this.$defaults.chart.options,
        });
        this.chart.options.onResize = chartResize;

        // starter event listeners for resizing chart
        const viewer = document.getElementById('viewer');
        this.resizeListener = new ResizeListener(viewer, this.onViewerResize);
      },
      resetPlot() {
        this.chart.data.datasets[0].data = this.$options.plot.nullData;
        this.chart.update();
      },
      async updatePlot(updateToothForm=false) {
        // Try updating plot data.
        // https://www.chartjs.org/docs/latest/developers/updates.html
        // NOTE: Too much recursion encountered when trying to call formGeom validate.
        // await this.$refs.formGeom.validate();

        // PLACEHOLDER {UX} draw circles: base, theoretical, tip, root
        // PLACEHOLDER {UX} add legend like GearMacroModeler

        try {
          // initialize
          let computedGeom = {};
          const tipRadiusCoefficient = parseFloat(this.formGeom.tipRadiusCoefficient);

          // compute tip radius params
          if (this.formGeom.tipRadiusMax || tipRadiusCoefficient > 0) {
            // pre-condition geom data; converts angular units to radians
            const geom = this.mapForms2Geom();
            this.$handlers.tool.convertPinionCutterGeom({geom}, this.$options.units, undefined);

            // fetch computed pinion cutter and update form
            computedGeom = await this.queryCompute(geom);
            this.formGeom.tipRadiusCoefficient = computedGeom.tipRadiusCoefficient;
          }

          // compute coordinates for plotting
          if (updateToothForm) this.$refs.formGeom.updateToothForm();
          const [x, y] = pinionCutterCoordinates(this.formGeom, computedGeom, this.$options.units.angle);
          let pts = points(x, y);

          if (!pts) {
            this.resetPlot()
          } else {
            this.updatePlotData(pts);
            this.updatePlotAxes();
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(e.message);
          this.resetPlot()
        }

        // FOR REFERENCE ONLY
        // let el = this.$refs.plot;
        // console.log('PLOT', el);
        // console.log('STYLE', el.style);
        // console.log('COMPUTED STYLE', getComputedStyle(el));
        // console.log('HEIGHT', el.style.height);
        // console.log('HEIGHT', el.clientHeight);
        // console.log('WIDTH', el.clientWidth);
        // console.log(Number(getComputedStyle(el).getPropertyValue('height').replace('px','')));
      },
      updatePlotData(pts) {
        // Update plot data of the first dataset.
        // PLACEHOLDER {UX} add computed datasets: profile shift (line), reference circle
        this.chart.data.datasets[0].data = pts;
        this.chart.update();
      },
      updatePlotAxes() {
        // Update plot axes to maintain an aspect ratio based on canvas size.
        // https://www.chartjs.org/docs/latest/developers/updates.html

        this.chart.options.onResize(this.chart);
        this.chart.update();
      },
      onViewerResize() {
        // Resize chart canvas to fit its parent, and update plot axes to maintain aspect ratio.

        this.$refs.plot.style.width = '100%';
        this.updatePlotAxes();
      },
    },
    beforeDestroy() {
      // remove resize listener and observer
      this.resizeListener.destroy();
    }
  }
</script>

<style scoped src="../../assets/css/form.css"/>
<style scoped lang="scss" src="../../assets/css/layout-tab-pane-1-2.scss"/>
<style scoped lang="scss" src="../../assets/css/viewer-tab.scss"/>
<style scoped>
    #plot-units {
        display: block;
        text-align: right;
        margin-right: 10pt;
        font-size: .7em;
    }
</style>
